import React from "react";
import { Link } from "react-router-dom";
import cn from "classnames";
import { FORMAT_LABEL, countLabel, progressPct, formatPrice } from "./learnApi";

const LockIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M5 7V5a3 3 0 016 0v2" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

/**
 * The single card used everywhere in the hub (rows, grid, up-next).
 * 16:9 still, count chip, New/Completed pill, thin progress bar, then
 * eyebrow → title → subtitle.
 *
 *   disabled   coming-soon mode: renders as a non-link with a "Coming soon"
 *              veil so nothing opens.
 *   locked     package-only course the viewer hasn't unlocked: lock pill +
 *              "from $499" tag; still links to the course landing where the
 *              unlock sheet lives.
 */
const CourseCard = ({ course, className, showProgress = true, disabled = false, priceFrom = null }) => {
  if (!course) return null;
  const pct = progressPct(course);
  const done = pct >= 100;
  const inProgress = pct > 0 && !done;
  const locked = Boolean(course.locked);
  const isSummit = course.access === "package";

  const body = (
    <>
      <div className="learn-card__media">
        {course.cover_image_url ? (
          <img src={course.cover_image_url} alt="" loading="lazy" />
        ) : (
          <div className="learn-card__media-fallback" />
        )}
        {disabled ? (
          <span className="learn-pill learn-pill--soon">Coming soon</span>
        ) : locked ? (
          <span className="learn-pill learn-pill--locked"><LockIcon /> {priceFrom ? `From ${formatPrice(priceFrom)}` : "Unlock"}</span>
        ) : done ? (
          <span className="learn-pill learn-pill--done">Completed</span>
        ) : isSummit ? (
          <span className="learn-pill learn-pill--summit">Summit 2026</span>
        ) : course.is_new ? (
          <span className="learn-pill">New</span>
        ) : null}
        <span className="learn-card__count">{countLabel(course)}</span>
        {showProgress && inProgress && !disabled && (
          <span className="learn-progress" aria-label={`${pct}% complete`}>
            <span className="learn-progress__bar" style={{ width: `${pct}%` }} />
          </span>
        )}
        {disabled && <span className="learn-card__veil" aria-hidden="true" />}
      </div>
      <div className="learn-card__body">
        <div className="learn-eyebrow">
          {FORMAT_LABEL[course.format] || "Course"}
          {course.topic ? <> · {course.topic}</> : null}
        </div>
        <h3 className="learn-card__title">{course.title}</h3>
        {course.subtitle && <p className="learn-card__subtitle">{course.subtitle}</p>}
      </div>
    </>
  );

  const classes = cn(
    "learn-card",
    { "learn-card--guide": course.format === "guide", "learn-card--disabled": disabled, "learn-card--locked": locked },
    className
  );

  if (disabled) {
    return (
      <div className={classes} aria-disabled="true" title="Coming soon">
        {body}
      </div>
    );
  }
  return (
    <Link to={`/learn/${course.slug}`} className={classes}>
      {body}
    </Link>
  );
};

export default CourseCard;
